import React, { useMemo } from 'react';
import { principleSegmentOf, formatPercentage, segmentHeadcountsFromCoverage } from '../../utils/trainingUtils';
import { ORDERED_SEGMENTS, SEGMENT_DISPLAY_LABELS } from '../../constants/trainingConstants';
import { TrainingIcon } from '../TrainingIcon';

/**
 * BRSR "Percentage coverage by training and awareness programmes on any of the
 * principles" table — one row per stakeholder segment (BoD, KMP, Employees,
 * Workers), each with programs held, principles touched, and the % of that
 * segment's headcount reached. The segment headcount (the denominator/total)
 * comes from the optimized-analytics `categoryCoverage`; programs, principles and
 * covered persons come from training attendance.
 */
export const PrincipleCoverageBySegment = ({ trainingData, categoryCoverage }) => {
  const rows = useMemo(() => {
    const principleKeyById = {};
    if (trainingData?.length && trainingData[0].allPrinciples) {
      trainingData[0].allPrinciples.forEach((principle, index) => {
        principleKeyById[principle.id] = `P${index + 1}`;
      });
    }

    const totals = segmentHeadcountsFromCoverage(categoryCoverage);

    const stats = {};
    ORDERED_SEGMENTS.forEach(segment => {
      stats[segment] = { programs: new Set(), principles: new Set(), covered: new Set() };
    });

    (trainingData || []).forEach(training => {
      if (!training) return;

      const trainingPrincipleKeys = (training.principles || [])
        .map(principle => principleKeyById[principle?.id])
        .filter(Boolean);

      (training.attendantUsers || []).forEach(attendant => {
        if (!attendant) return;
        const segment = principleSegmentOf(attendant);
        const employeeId = attendant.id;
        if (!stats[segment] || !employeeId) return;

        stats[segment].programs.add(training.id);
        stats[segment].covered.add(employeeId);
        trainingPrincipleKeys.forEach(key => stats[segment].principles.add(key));
      });
    });

    return ORDERED_SEGMENTS.map(segment => {
      const total = totals[segment] || 0;
      const covered = stats[segment].covered.size;
      const sortedPrinciples = [...stats[segment].principles].sort(
        (a, b) => Number(a.slice(1)) - Number(b.slice(1))
      );

      return {
        segment,
        total,
        programsHeld: stats[segment].programs.size,
        principles: sortedPrinciples.length ? sortedPrinciples.join(', ') : '-',
        coveragePercent: formatPercentage(covered, total)
      };
    });
  }, [trainingData, categoryCoverage]);

  return (
    <div className="trd-section">
      <div className="trd-section-head">
        <span className="trd-section-icon"><TrainingIcon name="grid" size={18} /></span>
        <div>
          <h2 className="trd-section-title">Percentage Coverage by Training and Awareness Programmes on Principles</h2>
        </div>
      </div>

      <div className="trd-block">
        <div className="trd-table-wrap">
          <table className="trd-table">
            <thead>
              <tr>
                <th className="trd-th-label">Segment</th>
                <th>Total (A)</th>
                <th>Programs held</th>
                <th>Topics / principles covered and its impact</th>
                <th>
                  <div>% covered</div>
                  <span className="trd-th-sub">of persons in category</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.segment} className="trd-tr">
                  <td className="trd-td-label">{SEGMENT_DISPLAY_LABELS[row.segment] || row.segment}</td>
                  <td>{row.total}</td>
                  <td>{row.programsHeld}</td>
                  <td>{row.principles}</td>
                  <td><span className="trd-num trd-num--green">{row.coveragePercent}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
